import { useState } from 'react';
import uploadsApi from '@/api/uploads';

const CONTENT_TYPES = {
  json: 'application/json',
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  csv: 'text/csv',
  txt: 'text/plain',
};
export const ACCEPT = Object.keys(CONTENT_TYPES)
  .map(ext => `.${ext}`)
  .join(',');
export const MAX_FILES = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const getContentType = file => CONTENT_TYPES[file.name.split('.').pop().toLowerCase()];

const useFeedbackAttachments = () => {
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');

  const handleFilesSelected = event => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';

    const invalid = selected.find(file => !getContentType(file) || file.size > MAX_FILE_SIZE);
    if (invalid) {
      setFileError(
        getContentType(invalid)
          ? `${invalid.name} is larger than 10 MB`
          : `${invalid.name} is not a supported file type`,
      );
      return;
    }
    if (files.length + selected.length > MAX_FILES) {
      setFileError(`You can attach up to ${MAX_FILES} files`);
      return;
    }

    setFileError('');
    setFiles([...files, ...selected]);
  };

  const removeFile = index => {
    setFileError('');
    setFiles(files.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (!files.length) return [];

    const { data } = await uploadsApi.getPresignedUrls({
      path: 'bug-reports',
      files: files.map(file => ({
        filename: `${crypto.randomUUID()}-${file.name}`,
        content_type: getContentType(file),
      })),
    });

    await Promise.all(
      data.files.map(({ upload_url }, i) => uploadsApi.uploadFile(upload_url, files[i], getContentType(files[i]))),
    );

    return data.files.map(({ file_url }) => file_url);
  };

  const resetAttachments = () => {
    setFiles([]);
    setFileError('');
  };

  return {
    files,
    fileError,
    setFileError,
    handleFilesSelected,
    removeFile,
    uploadFiles,
    resetAttachments,
  };
};

export default useFeedbackAttachments;
